import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../providers/ThemeProvider';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../firebase';
import { collection, collectionGroup, query, where, getDocs, doc, getDoc, orderBy, Timestamp, limit } from 'firebase/firestore';
import { CalendarIcon, ClockIcon, VideoIcon, MapPinIcon, LoaderIcon } from 'lucide-react';
import { selfLinkStudentToTeachers } from '../../services/studentService';
interface UpcomingLesson {
  id: string;
  teacherId: string;
  teacherName: string;
  teacherAvatar: string;
  instrument: string;
  date: string;
  time: string;
  duration: string;
  format: 'Virtual' | 'In-person';
  status: string;
  meetingLink?: string;
  notes?: string;
  taskTitle?: string;
  isNext: boolean;
}
export const StudentDashboard = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const {
    currentUser
  } = useAuth();
  const [studentName, setStudentName] = useState('');
  const [upcomingLessons, setUpcomingLessons] = useState<UpcomingLesson[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (!currentUser?.uid) return;
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        console.log('🔍 [StudentDashboard] ===== DIAGNOSIS START =====');
        console.log('🔍 [StudentDashboard] currentUser.uid:', currentUser.uid);
        console.log('🔍 [StudentDashboard] currentUser.email:', currentUser.email);
        // Step 1: Get student's first name from users/{uid}
        const userDocRef = doc(db, 'users', currentUser.uid);
        const userDoc = await getDoc(userDocRef);
        console.log('🔍 [StudentDashboard] users/{uid} doc exists:', userDoc.exists());
        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('🔍 [StudentDashboard] userData.email:', userData.email);
          console.log('🔍 [StudentDashboard] userData.firstName:', userData.firstName);
          setStudentName(userData.firstName || userData.name || 'Student');
          // Step 1b: Self-link
          const studentEmail = userData.email || currentUser.email;
          console.log('🔍 [StudentDashboard] Email used for self-link:', studentEmail);
          if (studentEmail) {
            try {
              const linkCount = await selfLinkStudentToTeachers(studentEmail, currentUser.uid);
              console.log('🔍 [StudentDashboard] selfLinkStudentToTeachers result:', linkCount, 'new links created');
            } catch (linkError) {
              console.error('❌ [StudentDashboard] selfLinkStudentToTeachers FAILED:', linkError);
            }
          } else {
            console.warn('⚠️ [StudentDashboard] No email found — cannot self-link');
          }
        } else {
          console.warn('⚠️ [StudentDashboard] No users/{uid} document found for:', currentUser.uid);
        }
        // Step 2: Find all teacher-student links via collectionGroup('students')
        console.log('🔍 [StudentDashboard] currentUser.uid exact value:', JSON.stringify(currentUser.uid), 'length:', currentUser.uid.length);
        // DIAGNOSTIC: Log Firebase project to confirm correct database
        console.log('🔍 [StudentDashboard] FIREBASE PROJECT CHECK:', {
          projectId: db.app.options.projectId,
          appName: db.app.name,
          authUid: currentUser.uid,
          authEmail: currentUser.email
        });
        // DIAGNOSTIC: Direct document read — bypasses collectionGroup entirely
        // If you know the exact path, paste it here to test direct access
        // e.g. teachers/TEACHER_UID/students/STUDENT_DOC_ID
        try {
          // First, try reading the teacher's students collection directly
          // by querying ALL teachers' top-level docs to find which ones have students
          const teachersSnap = await getDocs(collection(db, 'teachers'));
          console.log('🔍 [StudentDashboard] DIAGNOSTIC: teachers collection has', teachersSnap.size, 'docs');
          for (const teacherDoc of teachersSnap.docs) {
            const studentsCol = collection(db, 'teachers', teacherDoc.id, 'students');
            const studentsInTeacher = await getDocs(studentsCol);
            if (studentsInTeacher.size > 0) {
              console.log(`🔍 [StudentDashboard] DIAGNOSTIC: teacher ${teacherDoc.id} has ${studentsInTeacher.size} students:`);
              studentsInTeacher.docs.forEach(sDoc => {
                const sData = sDoc.data();
                console.log(`   📄 ${sDoc.ref.path}`, {
                  email: sData.email,
                  linkedUid: sData.linkedUid || '(NOT SET)',
                  name: sData.name,
                  matchesMe: sData.linkedUid === currentUser.uid
                });
              });
            }
          }
        } catch (directReadError: any) {
          console.error('❌ [StudentDashboard] DIAGNOSTIC direct read failed:', directReadError?.code || directReadError?.message);
          console.error('   If "permission-denied": Firestore rules block reading teachers collection for students.', '   The collectionGroup rule allows reading students/ docs, but direct collection reads require teacher ownership.');
        }
        // Step 2a: DIAGNOSTIC — unfiltered collectionGroup query to verify access
        try {
          const allStudentsSnap = await getDocs(collectionGroup(db, 'students'));
          console.log('🔍 [StudentDashboard] DIAGNOSTIC: unfiltered collectionGroup("students") returned', allStudentsSnap.size, 'total docs');
          // Check if our linkedUid exists in any of these docs
          const matchingDocs = allStudentsSnap.docs.filter(d => {
            const linkedUid = d.data().linkedUid;
            const uid = currentUser.uid;
            const matches = String(linkedUid).trim() === String(uid).trim();
            console.log('🔍 [StudentDashboard] DIAGNOSTIC client-side filter:', {
              path: d.ref.path,
              linkedUid: JSON.stringify(linkedUid),
              uid: JSON.stringify(uid),
              matches
            });
            return matches;
          });
          console.log('🔍 [StudentDashboard] DIAGNOSTIC: docs with matching linkedUid (client-side filter):', matchingDocs.length);
          matchingDocs.forEach(d => {
            const data = d.data();
            console.log('🔍 [StudentDashboard] DIAGNOSTIC match:', {
              path: d.ref.path,
              linkedUid: data.linkedUid,
              linkedUidType: typeof data.linkedUid,
              email: data.email,
              name: data.name
            });
          });
          // Also log all docs that have ANY linkedUid field set
          const docsWithLinkedUid = allStudentsSnap.docs.filter(d => d.data().linkedUid);
          console.log('🔍 [StudentDashboard] DIAGNOSTIC: total docs with linkedUid field set:', docsWithLinkedUid.length);
          docsWithLinkedUid.forEach(d => {
            const data = d.data();
            const storedVal = data.linkedUid;
            const expectedVal = currentUser.uid;
            const matchResult = String(storedVal).trim() === String(expectedVal).trim();
            console.log('🔍 [StudentDashboard] DIAGNOSTIC linkedUid doc:', {
              path: d.ref.path,
              linkedUid: JSON.stringify(storedVal),
              linkedUidType: typeof storedVal,
              matchesCurrentUser: matchResult
            });
          });
          // Store matchingDocs for fallback use below
          var clientSideMatchedDocs = matchingDocs;
        } catch (diagError: any) {
          console.error('❌ [StudentDashboard] DIAGNOSTIC unfiltered query failed:', diagError?.code || diagError?.message || diagError);
          var clientSideMatchedDocs: any[] = [];
        }
        // Step 2b: Filtered query by linkedUid
        console.log('🔍 [StudentDashboard] Querying collectionGroup("students") where linkedUid ==', JSON.stringify(currentUser.uid));
        let studentsSnap;
        try {
          const studentsQuery = query(collectionGroup(db, 'students'), where('linkedUid', '==', currentUser.uid));
          studentsSnap = await getDocs(studentsQuery);
          console.log('🔍 [StudentDashboard] linkedUid query results:', studentsSnap.size, 'docs');
          if (studentsSnap.size > 0) {
            studentsSnap.docs.forEach(d => {
              console.log('🔍 [StudentDashboard] matched doc:', d.ref.path, d.data());
            });
          }
        } catch (queryError: any) {
          console.error('❌ [StudentDashboard] linkedUid collectionGroup query FAILED:', queryError?.code || queryError?.message || queryError);
          console.error('   This likely means the COLLECTION_GROUP index for students/linkedUid is missing.', 'Deploy indexes with: firebase deploy --only firestore:indexes');
          // FALLBACK: Use client-side matched docs if available
          if (clientSideMatchedDocs && clientSideMatchedDocs.length > 0) {
            console.log('🔄 [StudentDashboard] FALLBACK: Using', clientSideMatchedDocs.length, 'client-side matched docs instead of failed server query');
            studentsSnap = {
              empty: false,
              size: clientSideMatchedDocs.length,
              docs: clientSideMatchedDocs
            } as any;
          } else {
            setUpcomingLessons([]);
            setIsLoading(false);
            return;
          }
        }
        // FALLBACK: If server query returned empty but client-side found matches,
        // use client-side results (index likely not deployed yet)
        if (studentsSnap.empty && clientSideMatchedDocs && clientSideMatchedDocs.length > 0) {
          console.warn('⚠️ [StudentDashboard] Server-side linkedUid query returned 0 docs, but client-side filter found', clientSideMatchedDocs.length, 'matches. Using client-side results as fallback.', 'This means the collectionGroup INDEX for linkedUid is likely not deployed yet.', 'Deploy with: firebase deploy --only firestore:indexes');
          studentsSnap = {
            empty: false,
            size: clientSideMatchedDocs.length,
            docs: clientSideMatchedDocs
          } as any;
        }
        if (studentsSnap.empty) {
          console.warn('⚠️ [StudentDashboard] No linked teacher records found. Possible causes:');
          console.warn('   1. selfLinkStudentToTeachers did not find email match');
          console.warn('   2. Firestore rules/indexes not deployed');
          console.warn('   3. Teacher stored a different email for this student');
          setUpcomingLessons([]);
          setIsLoading(false);
          console.log('🔍 [StudentDashboard] ===== DIAGNOSIS END (no links) =====');
          return;
        }
        // Build a map of { studentId → teacherId }
        const studentTeacherPairs: {
          studentId: string;
          teacherId: string;
        }[] = [];
        studentsSnap.docs.forEach(docSnap => {
          const pathSegments = docSnap.ref.path.split('/');
          const teacherId = pathSegments[1];
          const studentData = docSnap.data();
          // Use the studentId FIELD value (what lessons store), not the Firestore doc ID
          const studentIdForLessons = studentData.studentId || docSnap.id;
          console.log('🔍 [StudentDashboard] Found link:', {
            studentDocId: docSnap.id,
            studentIdField: studentData.studentId,
            studentIdUsedForLessons: studentIdForLessons,
            teacherId,
            path: docSnap.ref.path,
            data: {
              email: studentData.email,
              linkedUid: studentData.linkedUid
            }
          });
          studentTeacherPairs.push({
            studentId: studentIdForLessons,
            teacherId
          });
        });
        // Step 3: Query upcoming lessons from collectionGroup('calendar')
        // for each studentId, filtered to future lessons only
        const now = new Date();
        const allLessons: any[] = [];
        for (const pair of studentTeacherPairs) {
          const calendarQuery = query(collectionGroup(db, 'calendar'), where('studentId', '==', pair.studentId), where('startAt', '>=', Timestamp.fromDate(now)), orderBy('startAt', 'asc'), limit(10));
          const calendarSnap = await getDocs(calendarQuery);
          calendarSnap.docs.forEach(lessonDoc => {
            const data = lessonDoc.data();
            // Skip cancelled lessons
            if (data.status === 'CANCELED' || data.cancelled === true) {
              return;
            }
            allLessons.push({
              id: lessonDoc.id,
              teacherId: pair.teacherId,
              ...data
            });
          });
        }
        // Sort all lessons by startAt and take the first 3
        allLessons.sort((a, b) => {
          const aTime = a.startAt?.toDate ? a.startAt.toDate().getTime() : new Date(a.startAt).getTime();
          const bTime = b.startAt?.toDate ? b.startAt.toDate().getTime() : new Date(b.startAt).getTime();
          return aTime - bTime;
        });
        const topLessons = allLessons.slice(0, 3);
        // Step 4: Resolve teacher names from publicTeacherProfiles
        const teacherIds = [...new Set(topLessons.map(l => l.teacherId))];
        const teacherNameMap: Record<string, string> = {};
        for (const tid of teacherIds) {
          try {
            const profileDoc = await getDoc(doc(db, 'publicTeacherProfiles', tid));
            if (profileDoc.exists()) {
              const profileData = profileDoc.data();
              teacherNameMap[tid] = profileData.fullName || `${profileData.firstName || ''} ${profileData.lastName || ''}`.trim() || 'Teacher';
            } else {
              teacherNameMap[tid] = 'Teacher';
            }
          } catch {
            teacherNameMap[tid] = 'Teacher';
          }
        }
        // Step 4b: Resolve assigned tasks for each lesson
        const taskTitleMap: Record<string, string> = {};
        for (const tid of teacherIds) {
          try {
            const lessonIdsForTeacher = topLessons.filter(l => l.teacherId === tid).map(l => l.id);
            console.log('📘 [TaskLink] Teacher:', tid, 'lessonIdsForTeacher:', lessonIdsForTeacher);
            if (lessonIdsForTeacher.length === 0) continue;
            const tasksRef = collection(db, 'teachers', tid, 'tasks');
            const tasksSnap = await getDocs(tasksRef);
            console.log('📘 [TaskLink] Tasks fetched for teacher', tid, ':', tasksSnap.size, 'total tasks');
            tasksSnap.forEach(taskDoc => {
              const taskData = taskDoc.data();
              console.log('📘 [TaskLink] Task:', taskDoc.id, '| title:', taskData.title, '| lessonId:', taskData.lessonId || '(none)');
              if (taskData.lessonId && lessonIdsForTeacher.includes(taskData.lessonId) && taskData.title) {
                taskTitleMap[taskData.lessonId] = taskData.title;
                console.log('📘 [TaskLink] ✅ MATCHED! taskTitleMap[' + taskData.lessonId + '] =', taskData.title);
              }
            });
          } catch (taskError) {
            console.error('📘 [TaskLink] ❌ Error fetching tasks for teacher', tid, ':', taskError);
          }
        }
        console.log('📘 [TaskLink] Final taskTitleMap:', JSON.stringify(taskTitleMap));
        // Step 5: Transform into display format
        const formatted: UpcomingLesson[] = topLessons.map((lesson, index) => {
          const startDate = lesson.startAt?.toDate ? lesson.startAt.toDate() : new Date(lesson.startAt);
          const teacherFullName = teacherNameMap[lesson.teacherId] || 'Teacher';
          const initials = teacherFullName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2);
          // Format date
          const today = new Date();
          const tomorrow = new Date();
          tomorrow.setDate(today.getDate() + 1);
          let dateStr: string;
          if (startDate.toDateString() === today.toDateString()) {
            dateStr = 'Today';
          } else if (startDate.toDateString() === tomorrow.toDateString()) {
            dateStr = 'Tomorrow';
          } else {
            dateStr = startDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric'
            });
          }
          // Format time
          const timeStr = startDate.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
          });
          // Duration
          const durationMin = lesson.durationMinutes || 60;
          const durationStr = `${durationMin} min`;
          // Location type
          const location = (lesson.location || '').toLowerCase();
          const isOnline = location.includes('online') || location.includes('virtual') || location.includes('zoom') || location.includes('meet') || !!lesson.meetingLink;
          const format: 'Virtual' | 'In-person' = isOnline ? 'Virtual' : 'In-person';
          // Status display
          const rawStatus = (lesson.status || 'SCHEDULED').toUpperCase();
          let statusDisplay = 'Scheduled';
          if (rawStatus === 'SCHEDULED') statusDisplay = 'Confirmed';else if (rawStatus === 'COMPLETED') statusDisplay = 'Completed';
          return {
            id: lesson.id,
            teacherId: lesson.teacherId,
            teacherName: teacherFullName,
            teacherAvatar: initials,
            instrument: lesson.instrument || 'Music',
            date: dateStr,
            time: timeStr,
            duration: durationStr,
            format,
            status: statusDisplay,
            meetingLink: lesson.meetingLink,
            notes: lesson.notes || '',
            taskTitle: taskTitleMap[lesson.id] || '',
            isNext: index === 0
          };
        });
        setUpcomingLessons(formatted);
      } catch (error) {
        console.error('Error fetching student dashboard data:', error);
        setUpcomingLessons([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchDashboardData();
  }, [currentUser?.uid]);
  return <div className="min-h-screen p-6 md:p-8" data-id="element-6897">
      <div className="max-w-4xl mx-auto space-y-8" data-id="element-6898">
        {/* Welcome Header */}
        <div data-id="element-6899">
          <h1 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6900">
            Welcome back{studentName ? `, ${studentName}` : ''}
          </h1>
          <p className={`mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6901">
            {new Date().toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          })}
          </p>
        </div>

        {/* Upcoming Lessons Section */}
        <div data-id="element-6902">
          <h2 className={`text-xl font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6903">
            Upcoming Lessons
          </h2>

          {isLoading ? <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-navy-800 border-navy-700' : 'bg-white border-gray-200'}`} data-id="element-6904">
              <LoaderIcon className={`w-8 h-8 animate-spin mx-auto mb-3 ${isDark ? 'text-green-400' : 'text-green-500'}`} data-id="element-6905" />
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6906">
                Loading your lessons...
              </p>
            </div> : upcomingLessons.length > 0 ? <div className="space-y-3" data-id="element-6907">
              {upcomingLessons.map(lesson => <div key={lesson.id} className={`rounded-lg p-5 cursor-pointer transition-all duration-200 ease-out ${lesson.isNext ? isDark ? 'bg-navy-800 border border-blue-500/20 shadow-md shadow-blue-500/5 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-blue-500/15 hover:bg-navy-800/80' : 'bg-white border border-blue-200 shadow-sm shadow-blue-100 hover:-translate-y-0.5 hover:shadow-md hover:shadow-blue-200' : isDark ? 'bg-navy-800 border border-navy-800 hover:-translate-y-0.5 hover:bg-[#1e2a4a] hover:shadow-lg hover:shadow-blue-500/10' : 'bg-white border border-gray-200 shadow-sm hover:-translate-y-0.5 hover:shadow-md hover:bg-gray-50'}`} data-id="element-6908">
                  <div className="flex items-center justify-between gap-4" data-id="element-6909">
                    <div className="flex items-center gap-4 flex-1 min-w-0" data-id="element-6910">
                      {/* Teacher Avatar */}
                      <div className={`${lesson.isNext ? 'w-12 h-12' : 'w-10 h-10'} rounded-full flex items-center justify-center ${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold flex-shrink-0 ${isDark ? 'bg-blue-500/20 text-blue-400' : 'bg-blue-100 text-blue-600'}`} data-id="element-6911">
                        {lesson.teacherAvatar}
                      </div>

                      {/* Lesson info */}
                      <div className="flex-1 min-w-0" data-id="element-6912">
                        <div className="flex items-center gap-2 mb-1" data-id="element-6913">
                          <h3 className={`${lesson.isNext ? 'text-base' : 'text-sm'} font-semibold truncate ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6914">
                            {lesson.instrument}
                          </h3>
                          {lesson.isNext && <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-green-500/20 text-green-400' : 'bg-green-100 text-green-700'}`} data-id="element-6915">
                              {lesson.status}
                            </span>}
                          <span className={`text-xs px-2 py-0.5 rounded ${isDark ? 'bg-navy-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`} data-id="element-6916">
                            {lesson.duration}
                          </span>
                        </div>
                        <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} mb-1`} data-id="element-6917">
                          with {lesson.teacherName}
                        </p>
                        <div className="flex flex-wrap items-center gap-3 text-sm" data-id="element-6918">
                          <div className="flex items-center gap-1.5" data-id="element-6919">
                            <ClockIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6920" />
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6921">
                              {lesson.date} at {lesson.time}
                            </span>
                          </div>
                          <div className="flex items-center gap-1.5" data-id="element-6922">
                            {lesson.format === 'Virtual' ? <VideoIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6923" /> : <MapPinIcon className={`h-4 w-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6924" />}
                            <span className={isDark ? 'text-gray-300' : 'text-gray-700'} data-id="element-6925">
                              {lesson.format === 'Virtual' ? 'Online' : lesson.format}
                            </span>
                          </div>
                        </div>
                        {lesson.notes && <p className={`text-xs mt-1.5 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6926">
                            🎵 {lesson.notes}
                          </p>}
                        {lesson.taskTitle && <p className={`text-xs mt-1 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-6927">
                            📘 {lesson.taskTitle}
                          </p>}
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2 flex-shrink-0" data-id="element-6928">
                      {lesson.format === 'Virtual' && lesson.meetingLink && <button onClick={e => {
                  e.stopPropagation();
                  window.open(lesson.meetingLink, '_blank');
                }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${isDark ? 'bg-navy-700 hover:bg-navy-600 hover:border-blue-500/30 text-white border border-navy-600' : 'bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-200 hover:border-gray-300'}`} data-id="element-6929">
                          Open Link
                        </button>}
                    </div>
                  </div>
                </div>)}
            </div> :
        // Empty state
        <div className={`rounded-xl p-12 text-center border-2 border-dashed ${isDark ? 'bg-navy-800/50 border-navy-700' : 'bg-gray-50 border-gray-300'}`} data-id="element-6930">
              <div className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-navy-700' : 'bg-gray-200'}`} data-id="element-6931">
                <CalendarIcon className={`h-8 w-8 ${isDark ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-6932" />
              </div>
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-6933">
                No upcoming lessons yet
              </h3>
              <p className={`mb-6 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-6934">
                Your upcoming lessons will appear here.
              </p>
              <button onClick={() => navigate('/find-teachers')} className="px-6 py-2.5 bg-deep-pink hover:bg-burgundy text-white rounded-lg font-medium transition-colors" data-id="element-6935">
                Find a Teacher
              </button>
            </div>}
        </div>
      </div>
    </div>;
};